import { Row } from 'components/Layout';
import ReviewPayments from 'components/ReviewPayments';
import TransactionAllocator from 'components/TransactionAllocator';
import { type Transaction } from 'components/TransactionAllocator';
import { useLayoutContext } from 'contexts/LayoutContext';
import { ActionTransactionSubTypes } from 'further-types/lp/action';
import { CurrencyRateDetails } from 'further-types/lp/transaction';
import { CurrencyCode } from 'further-types/lp/vehicle';
import { usePagination } from 'further-ui/hooks';
import { ActionRefLabels } from 'further-ui/labels';
import { CalendarDay, numberToCurrencyString } from 'further-ui/utils';
import useTransactionTypeSelectorOptions from 'hooks/form/useTransactionTypeSelectorOptions';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import api from 'lib/trpcClient';
import { useEffect, useState } from 'react';
import PayablesOrReceivablesHistory from './PayablesOrReceivablesHistory';
import Table, { Column as TableColumn } from 'further-ui/components/Table';
import { styled } from '@mui/material/styles';
import TabsPanel from 'further-ui/components/TabsPanel';
import { Column } from 'further-ui/components/Layout';

const pageId = 'post-unrealised-fx-gains';

const ExpandedRowContainer = styled('div')(({ theme }) => ({
  backgroundColor: theme.palette.grey[50],
  borderTop: `1px solid ${theme.palette.grey[300]}`,
}));

type Props = {
  vehicleId: string;
  periodStart: CalendarDay;
  periodEnd: CalendarDay;
  currencyRateOverrides: Map<CurrencyCode, CurrencyRateDetails>;
  onCurrenciesLoaded: (
    currencies: Array<[CurrencyCode, CurrencyRateDetails]>,
  ) => void;
  onApproveSuccess?: () => void;
  approveRef: React.MutableRefObject<(() => Promise<void>) | null>;
  onViewRecord?: (recordId: string) => void;
};

const PayablesOrReceivables: React.FC<Props> = ({
  vehicleId,
  periodStart,
  periodEnd,
  currencyRateOverrides,
  onCurrenciesLoaded,
  onApproveSuccess,
  approveRef,
  onViewRecord,
}) => {
  const { vehicle } = useLayoutContext();

  const [transactionChanges, setTransactionChanges] = useState<
    Map<string, Array<Transaction>>
  >(new Map());
  const [expandedRowId, setExpandedRowId] = useState<string | null>(null);

  const pagination = usePagination({
    id: pageId,
    order: 'desc',
    orderBy: 'transactionDate',
  });
  const { transactionTypeOptions, isLoading: transactionTypesLoading } =
    useTransactionTypeSelectorOptions();

  const { data, isFetching, refetch } =
    api.unrealisedFxGainOrLoss.list.useQuery({
      vehicleId,
      periodStart,
      periodEnd,
      page: pagination.page,
      rowsPerPage: pagination.rowsPerPage,
      order: pagination.order,
      orderBy: pagination.orderBy,
      ...(currencyRateOverrides
        ? { currencyRateOverrides: Object.fromEntries(currencyRateOverrides) }
        : {}),
    });

  useEffect(() => {
    if (!data?.viewDateCurrencyRates) return;
    onCurrenciesLoaded(data.viewDateCurrencyRates);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.viewDateCurrencyRates]);

  useEffect(() => {
    setTransactionChanges(new Map());
  }, [currencyRateOverrides]);

  useEffect(() => {
    void refetch();
  }, [currencyRateOverrides, refetch]);

  const mutationOpts = useHandleTrpcMutation();
  const { mutateAsync: createUnrealisedFXGains } =
    api.unrealisedFxGainOrLoss.create.useMutation(
      mutationOpts({
        successMessage: 'Created unrealised FX gains/losses successfully.',
      }),
    );

  const rowsForTable = data?.transactions.results ?? [];

  const postUnrealisedFXGains = async () => {
    const postUnrealisedFXGainsBody = rowsForTable.map((row) => {
      const transactions = transactionChanges.get(row.id);
      return {
        sourceTransactionId: row.id,
        transactions: (transactions ?? row.transactions).map((transaction) => ({
          transactionTypeId: transaction.transactionTypeId,
          operationType: transaction.operationType,
        })),
      };
    });

    await createUnrealisedFXGains({
      vehicleId,
      transactions: postUnrealisedFXGainsBody,
      viewDate: periodEnd,
      currencyRateOverrides: Object.fromEntries(currencyRateOverrides),
    });

    await onApproveSuccess?.();
  };

  approveRef.current = postUnrealisedFXGains;

  const handleTransactionChange = (
    rowId: string,
    newTransactions: Array<Transaction>,
  ) => {
    setTransactionChanges((prev) => {
      const newMap = new Map(prev);
      newMap.set(rowId, newTransactions);
      return newMap;
    });
  };

  const columns: Array<TableColumn<(typeof rowsForTable)[number]>> = [
    {
      key: 'actionRef',
      label: 'Action',
      render: (row) => ActionRefLabels[row.actionRef],
      disableSorting: true,
    },
    {
      key: 'description',
      label: 'Description',
      disableSorting: true,
    },
    {
      key: 'transactionDate',
      label: 'Date',
      render: (row) => row.transactionDate.toLabel(),
    },
    {
      key: 'counterpartyLabel',
      label: 'Counterparty',
      disableSorting: true,
    },
    {
      key: 'unreconciledAmountTcy',
      label: 'Original value (TCY)',
      render: (row) =>
        numberToCurrencyString(
          row.calculationBreakdown?.unreconciledAmountTcy ??
            row.unreconciledAmountTcy,
          {
            fallback: '-',
            currency: row.currencyCode,
          },
        ),
      disableSorting: true,
    },
    {
      key: 'originalResidualValueLcy',
      label: 'Original value (LCY)',
      render: (row) =>
        numberToCurrencyString(
          row.calculationBreakdown?.originalResidualValueLcy,
          {
            fallback: '-',
            currency: vehicle?.baseCurrency,
          },
        ),
      disableSorting: true,
    },
    {
      key: 'runningTotalUnrealisedLcy',
      label: 'Running total unrealised',
      render: (row) =>
        numberToCurrencyString(
          row.calculationBreakdown?.runningTotalUnrealisedLcy,
          {
            fallback: '-',
            currency: vehicle?.baseCurrency,
            negativeInBrackets: true,
          },
        ),
      disableSorting: true,
    },
    {
      key: 'currentValueLcy',
      label: 'Current value (LCY)',
      render: (row) =>
        numberToCurrencyString(row.calculationBreakdown?.currentValueLcy, {
          fallback: '-',
          currency: vehicle?.baseCurrency,
        }),
      disableSorting: true,
    },
    {
      key: 'unrealisedGainOrLossLcy',
      label: 'Unrealised gain/loss (LCY)',
      render: (row) => {
        return numberToCurrencyString(row.unrealisedGainOrLossLcy, {
          fallback: '-',
          currency: vehicle?.baseCurrency,
          negativeInBrackets: true,
        });
      },
      disableSorting: true,
    },
  ];

  if (!vehicle) {
    return null;
  }

  const buildAllocatorTransactions = (
    row: (typeof rowsForTable)[number],
  ): Array<Transaction> => {
    const transactions = transactionChanges.get(row.id);

    if (transactions) {
      return transactions;
    }

    const fallbackTransactions: Array<Transaction> = row.transactions.map(
      (transaction) => ({
        ...transaction,
        transactionCurrency: row.currencyCode,
        transactionSubType: ActionTransactionSubTypes.CreditDebitPair,
      }),
    );

    return fallbackTransactions;
  };

  return (
    <Column spacing="lg">
      <Table
        columns={columns}
        rows={rowsForTable}
        isLoading={isFetching}
        pagination={{
          page: pagination.page,
          rowsPerPage: pagination.rowsPerPage,
          order: pagination.order,
          orderBy: pagination.orderBy,
          handleRequestSort: pagination.handleRequestSort,
          handleChangePage: pagination.handleChangePage,
          handleChangeRowsPerPage: pagination.handleChangeRowsPerPage,
        }}
        rowsTotalCount={data?.transactions.totalCount ?? 0}
        isExpandableRow={() => true}
        isExpandedRow={(row) => expandedRowId === row.id}
        onRowExpand={(row) =>
          setExpandedRowId((prev) => (prev === row.id ? null : row.id))
        }
        getExpandedContent={(row) => (
          <ExpandedRowContainer>
            <TabsPanel
              tabs={[
                {
                  key: 'accountTransactions',
                  label: 'Account transactions',
                  content: (
                    <Row fullWidth>
                      {!transactionTypesLoading && (
                        <TransactionAllocator
                          darkBackground
                          transactionTypeOptions={transactionTypeOptions}
                          transactions={buildAllocatorTransactions(row)}
                          onChange={(newTransactions) => {
                            handleTransactionChange(
                              row.id ?? '',
                              newTransactions,
                            );
                          }}
                          transactionCurrency={row.currencyCode}
                          vehicle={vehicle}
                          fxRate={row.currencyRate}
                        />
                      )}
                    </Row>
                  ),
                },
                {
                  key: 'paymentRecord',
                  label: 'Payment record',
                  content: (
                    <Row fullWidth>
                      <ReviewPayments
                        transactionId={row.transactions[0]?.id}
                        vehicleId={vehicleId}
                        currencyCode={row.currencyCode}
                        amountTcy={row.amountTcy}
                      />
                    </Row>
                  ),
                },
              ]}
            />
          </ExpandedRowContainer>
        )}
      />
      <PayablesOrReceivablesHistory
        vehicleId={vehicleId}
        onViewRecord={onViewRecord}
      />
    </Column>
  );
};

export default PayablesOrReceivables;
